import { useMutation } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useWorkspace } from '../app/WorkspaceProvider';
import { useAuth } from '../auth/AuthProvider';
import { ClapperRule, Wordmark } from '../components/Clapper';
import { AppLink } from '../components/Nav';
import { CheckIcon, UsersIcon } from '../components/icons';
import { Banner, Button, Spinner } from '../components/ui';
import { ApiError, errorMessage } from '../lib/api';
import { acceptInvite, roleDescription, roleLabel } from '../queries/orgs';

/**
 * Accepting an invitation. Rendered outside the app shell: the person opening
 * this may not have an account yet, and a nav bar full of things they cannot
 * reach would be noise.
 */

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-full items-center justify-center bg-slate-950 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <Wordmark />
        </div>
        <div className="card overflow-hidden">
          <ClapperRule />
          <div className="p-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

function failureTitle(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.isNotFound) return 'This invitation is no longer valid';
    if (error.status === 409) return 'This invitation has already been used';
    if (error.status === 403) return 'This invitation cannot be accepted';
  }
  return 'Could not accept the invitation';
}

function recoverable(error: unknown): boolean {
  if (error instanceof ApiError) return !(error.isNotFound || error.status === 409 || error.status === 403);
  return true;
}

export function InvitePage({ token }: { token: string }) {
  const { user, loading, signOut } = useAuth();
  const { refetch, select } = useWorkspace();

  const accept = useMutation({
    mutationFn: () => acceptInvite(token),
    onSuccess: (response) => {
      refetch();
      select(response.org.id);
    },
  });

  if (loading) {
    return (
      <Shell>
        <div className="flex items-center justify-center gap-2.5 py-6 text-chalk-faint">
          <Spinner />
          <span className="text-body">Checking your session…</span>
        </div>
      </Shell>
    );
  }

  if (!user) {
    return (
      <Shell>
        <h1 className="text-h3 text-chalk">You have been invited to a team on Slate</h1>
        <p className="mt-2 text-body text-chalk-dim">
          Sign in with the email address the invitation was sent to and we will add you to the
          workspace. If you do not have an account yet, creating one takes a moment. Use that same
          address.
        </p>
        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          <AppLink to="/login" className="sm:flex-1">
            <Button variant="primary" size="lg" className="w-full">
              Sign in
            </Button>
          </AppLink>
          <AppLink to="/signup" className="sm:flex-1">
            <Button size="lg" className="w-full">
              Create an account
            </Button>
          </AppLink>
        </div>
        <p className="mt-4 text-caption text-chalk-faint">
          Come back to this page afterwards; the link keeps working until it expires.
        </p>
      </Shell>
    );
  }

  if (accept.data) {
    return (
      <Shell>
        <div className="flex items-center gap-2 text-go">
          <CheckIcon size={18} />
          <span className="text-caption uppercase tracking-[0.08em]">Joined</span>
        </div>
        <h1 className="mt-2 text-h3 text-chalk">You are in: {accept.data.org.name}</h1>
        <p className="mt-2 text-body text-chalk-dim">
          You joined as {roleLabel(accept.data.role).toLowerCase()}.{' '}
          {roleDescription(accept.data.role)}
        </p>
        <AppLink to="/" className="mt-5 block">
          <Button variant="primary" size="lg" className="w-full">
            Go to Slate
          </Button>
        </AppLink>
        <p className="mt-4 text-caption text-chalk-faint">
          Switch between your personal library and {accept.data.org.name} from the workspace picker
          in the header.
        </p>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="flex items-center gap-2 text-chalk-faint">
        <UsersIcon size={18} />
        <span className="text-caption uppercase tracking-[0.08em]">Invitation</span>
      </div>
      <h1 className="mt-2 text-h3 text-chalk">Join a team on Slate</h1>
      <p className="mt-2 text-body text-chalk-dim">
        Accepting adds you to the workspace and to its seat count. Your personal library stays
        separate and stays yours.
      </p>

      <p className="mt-4 text-caption text-chalk-faint">
        Signed in as <span className="text-chalk-dim">{user.email}</span>. Invitations only work for
        the address they were sent to.
      </p>

      {accept.isError ? (
        <div className="mt-4">
          <Banner tone="danger" title={failureTitle(accept.error)}>
            {errorMessage(accept.error)}
            {accept.error instanceof ApiError && accept.error.status === 409
              ? ' If that was you, you are already a member. Head to Slate and pick the workspace from the header.'
              : ''}
          </Banner>
        </div>
      ) : null}

      {accept.isError && !recoverable(accept.error) ? (
        <AppLink to="/" className="mt-5 block">
          <Button variant="primary" size="lg" className="w-full">
            Go to Slate
          </Button>
        </AppLink>
      ) : (
        <Button
          variant="primary"
          size="lg"
          className="mt-5 w-full"
          busy={accept.isPending}
          onClick={() => accept.mutate()}
        >
          {accept.isError ? 'Try again' : 'Accept invitation'}
        </Button>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <AppLink to="/" className="text-caption text-chalk-faint hover:text-chalk-dim">
          Skip for now
        </AppLink>
        <button
          type="button"
          onClick={() => void signOut()}
          className="text-caption text-chalk-faint transition-colors duration-120 hover:text-chalk-dim"
        >
          Wrong account? Sign out
        </button>
      </div>
    </Shell>
  );
}
